/**
 * CaseDetailPage — карточка тикета: фото, заказ и его журнал, что ещё было на
 * машине в это время, переписка и кнопки решений. Root cause обязателен при
 * закрытии — из него собирается статистика «что повторяется».
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  getCaseDetail, casePhotoUrl, assignCase, messageCase, refundCaseCard, requestCaseBank, revealCaseBank,
  markCasePaid, resolveCase, rejectCase, reopenCase, type CaseDetail, type AuthUser,
  listCaseTemplates, createCaseTemplate, updateCaseTemplate, deleteCaseTemplate, type CaseTemplate,
} from '../api/client';
import { CATEGORY_LABEL, ROOT_CAUSE_LABEL, StatusBadge } from './CasesPage';

const OPEN = new Set(['new', 'in_progress', 'waiting_customer', 'waiting_bank_account', 'to_pay']);

const fmt = (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : '—');

export function CaseDetailPage({ user }: { user: AuthUser | null }) {
  const { id = '' } = useParams();
  const [c, setC] = useState<CaseDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [photo, setPhoto] = useState<string | null>(null);

  const load = useCallback(() => {
    getCaseDetail(id).then((d) => { setC(d); setError(null); }).catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function run(fn: () => Promise<CaseDetail>) {
    setBusy(true); setError(null);
    try { setC(await fn()); } catch (e) { setError(e instanceof Error ? e.message : 'Action failed'); } finally { setBusy(false); }
  }

  if (error && !c) return <div className="card"><div className="card-body" style={{ color: 'var(--color-offline)' }}>Error: {error}</div></div>;
  if (!c) return <div className="card"><div className="card-body"><p className="text-muted">Loading…</p></div></div>;

  const open = OPEN.has(c.status);
  const s = c.session;

  return (
    <div>
      <div className="flex items-center justify-between mb-6" style={{ flexWrap: 'wrap', gap: 8 }}>
        <div>
          <Link to="/cases" style={{ fontSize: '0.85rem' }}>← Complaints</Link>
          <h2 style={{ margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 10 }}>
            Complaint #{c.number} <StatusBadge status={c.status} />
          </h2>
          <div className="text-muted" style={{ fontSize: '0.85rem' }}>
            {CATEGORY_LABEL[c.category] ?? c.category}{c.kioskReason && <> · kiosk said: {c.kioskReason}</>} · filed {fmt(c.createdAt)} · via {c.source}
          </div>
        </div>
        <div className="flex gap-2">
          {open && (c.assigneeId !== user?.id) && <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void run(() => assignCase(c.id))}>Take it</button>}
          {!open && !c.purgedAt && <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => void run(() => reopenCase(c.id))}>Reopen</button>}
        </div>
      </div>

      {error && <div className="card mb-6"><div className="card-body" style={{ color: 'var(--color-offline)' }}>Error: {error}</div></div>}
      {!c.mailConfigured && open && (
        <div className="card mb-6"><div className="card-body" style={{ color: '#a16207', background: 'rgba(234,179,8,0.12)' }}>
          ⚠ Email is not configured on the server (RESEND_API_KEY) — the customer will <b>not</b> receive our replies. Call or email them by hand.
        </div></div>
      )}

      <div className="detail-grid">
        {/* Левая колонка: что рассказал клиент */}
        <div>
          <div className="card mb-6">
            <div className="card-header">Customer</div>
            <div className="card-body">
              <table className="info-table"><tbody>
                <tr><td>Email</td><td>{c.email ?? <span className="text-muted">purged</span>} <span className="text-muted">({c.lang})</span></td></tr>
                {c.assignee && <tr><td>Assignee</td><td>{c.assignee}</td></tr>}
                {c.description && <tr><td>Description</td><td style={{ whiteSpace: 'pre-wrap' }}>{c.description}</td></tr>}
                {!s && (
                  <>
                    <tr><td>Reported machine</td><td>{c.reportedMachineId ? <Link to={`/machines/${c.reportedMachineId}`}>{c.machineName ?? `#${c.reportedMachineId}`}</Link> : '—'}</td></tr>
                    <tr><td>Reported time</td><td>{fmt(c.reportedAt)}</td></tr>
                    <tr><td>Reported paid</td><td>{c.reportedPaidCzk != null ? `${c.reportedPaidCzk} Kč` : '—'} · {c.reportedPayment ?? '—'}</td></tr>
                    {c.code && <tr><td>Code given</td><td>{c.code} <span className="text-muted">(no such order)</span></td></tr>}
                  </>
                )}
                <tr><td>Customer page</td><td><a href={c.caseUrl} target="_blank" rel="noreferrer" style={{ fontSize: '0.85rem' }}>open ↗</a></td></tr>
              </tbody></table>

              {c.photos.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                  {c.photos.map((p) => (
                    <img key={p.id} src={casePhotoUrl(c.id, p.id)} alt="" onClick={() => setPhoto(casePhotoUrl(c.id, p.id))}
                      style={{ width: 140, height: 140, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--color-border)', cursor: 'zoom-in' }} />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card mb-6">
            <div className="card-header">Order{s?.code && <> · <Link to={`/sessions?code=${s.code}`}>{s.code}</Link></>}</div>
            <div className="card-body">
              {!s ? <p className="text-muted">No order linked — find it in Sessions by machine and time.</p> : (
                <table className="info-table"><tbody>
                  <tr><td>Machine</td><td>{s.machineId ? <Link to={`/machines/${s.machineId}`}>{c.machineName ?? `#${s.machineId}`}</Link> : '—'}</td></tr>
                  <tr><td>State</td><td>{s.state}{s.refunded && ' · refunded'}</td></tr>
                  <tr><td>Price</td><td>{s.priceCzk != null ? `${s.priceCzk} Kč` : '—'} · {s.paymentMethod ?? 'payment unknown'} · {s.paid ? `paid ${fmt(s.paidAt)}` : 'not marked paid'}</td></tr>
                  <tr><td>Created</td><td>{fmt(s.createdAt)}</td></tr>
                  <tr><td>Stripe</td><td>{s.hasPaymentIntent ? 'card payment on file → refund possible here' : 'no card payment (cash?)'}</td></tr>
                </tbody></table>
              )}
              {c.trail.length > 0 && (
                <details style={{ marginTop: 10 }}>
                  <summary style={{ cursor: 'pointer', fontSize: '0.85rem' }}>Order journal ({c.trail.length})</summary>
                  <ul style={{ fontSize: '0.82rem', paddingLeft: 18, marginTop: 6 }}>
                    {c.trail.map((e, i) => (
                      <li key={i}><span className="text-muted">{fmt(e.at)}</span> <b>{e.type}</b> <span className="text-muted">{e.terminal}</span>
                        {e.payload && <code style={{ fontSize: '0.75rem', marginLeft: 6 }}>{JSON.stringify(e.payload).slice(0, 160)}</code>}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          </div>

          <div className="card mb-6">
            <div className="card-header">Around that time on this machine (±24 h)</div>
            <div className="card-body" style={{ fontSize: '0.85rem' }}>
              <Nearby title="Other complaints" items={(c.nearby.cases ?? []).map((x) => <Link key={x.id} to={`/cases/${x.id}`}>#{x.number} {CATEGORY_LABEL[x.category] ?? x.category} · {fmt(x.createdAt)}</Link>)} />
              <Nearby title="Alerts" items={(c.nearby.alerts ?? []).map((a, i) => <span key={i}>{a.type} — {a.message} · {fmt(a.createdAt)}{a.resolvedAt ? '' : ' · still active'}</span>)} />
              <Nearby title="Kiosk events" items={(c.nearby.events ?? []).map((e, i) => <span key={i}>{e.type} <code style={{ fontSize: '0.75rem' }}>{JSON.stringify(e.details ?? {}).slice(0, 120)}</code> · {fmt(e.at)}</span>)} />
              {!c.machineId && <p className="text-muted">Machine unknown — nothing to compare.</p>}
            </div>
          </div>
        </div>

        {/* Правая колонка: действия и журнал */}
        <div>
          {open && <Actions c={c} busy={busy} run={run} />}

          {(c.status === 'resolved' || c.status === 'rejected') && (
            <div className="card mb-6">
              <div className="card-header">Outcome</div>
              <div className="card-body">
                <table className="info-table"><tbody>
                  <tr><td>Resolution</td><td>{c.resolution?.replace(/_/g, ' ')}{c.refundCzk != null && <> · {c.refundCzk} Kč</>}</td></tr>
                  <tr><td>Root cause</td><td>{c.rootCause ? (ROOT_CAUSE_LABEL[c.rootCause] ?? c.rootCause) : '—'}</td></tr>
                  <tr><td>Closed</td><td>{fmt(c.resolvedAt)} by {c.resolvedByName ?? '—'}</td></tr>
                  {c.resolutionNote && <tr><td>Told customer</td><td style={{ whiteSpace: 'pre-wrap' }}>{c.resolutionNote}</td></tr>}
                  {c.purgedAt && <tr><td>Purged</td><td>{fmt(c.purgedAt)} (email, account, photos deleted)</td></tr>}
                </tbody></table>
              </div>
            </div>
          )}

          <div className="card mb-6">
            <div className="card-header">Journal</div>
            <div className="card-body">
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '0.85rem' }}>
                {c.events.map((e) => (
                  <li key={e.id} style={{ padding: '6px 0', borderBottom: '1px solid var(--color-border)' }}>
                    <span className="text-muted">{fmt(e.createdAt)}</span> · <b>{e.type.replace(/_/g, ' ')}</b>
                    {e.actorName && <> · {e.actorName}</>}
                    {e.type === 'email_failed' || e.type === 'email_skipped' ? <span style={{ color: '#b91c1c' }}> — {String(e.payload?.['error'] ?? e.payload?.['reason'] ?? '')}</span> : null}
                    {(e.type === 'message_to_customer' || e.type === 'customer_reply') && (
                      <div style={{ whiteSpace: 'pre-wrap', marginTop: 4, padding: '6px 10px', borderRadius: 6, background: e.type === 'customer_reply' ? 'rgba(59,130,246,0.08)' : 'var(--color-bg)' }}>
                        {String(e.payload?.['text'] ?? '')}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {photo && (
        <div onClick={() => setPhoto(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, cursor: 'zoom-out' }}>
          <img src={photo} alt="" style={{ maxWidth: '95vw', maxHeight: '95vh', objectFit: 'contain' }} />
        </div>
      )}
    </div>
  );
}

function Nearby({ title, items }: { title: string; items: React.ReactNode[] }) {
  if (items.length === 0) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{title}</div>
      <ul style={{ paddingLeft: 18, margin: '4px 0 0' }}>{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
    </div>
  );
}

type Mode = 'message' | 'refund_card' | 'bank' | 'close' | 'reject';

function Actions({ c, busy, run }: { c: CaseDetail; busy: boolean; run: (fn: () => Promise<CaseDetail>) => Promise<void> }) {
  const [mode, setMode] = useState<Mode | null>(null);
  const [text, setText] = useState('');
  const [ask, setAsk] = useState(true);
  const [amount, setAmount] = useState<string>(String(c.session?.priceCzk ?? c.reportedPaidCzk ?? c.refundCzk ?? ''));
  const [cause, setCause] = useState('');
  const [resolution, setResolution] = useState('already_refunded');
  const [revealed, setRevealed] = useState<string | null>(null);

  const reset = () => { setMode(null); setText(''); };
  const causeOk = cause.trim().length > 0;

  return (
    <div className="card mb-6">
      <div className="card-header">Actions</div>
      <div className="card-body">
        {c.status === 'to_pay' && (
          <div style={{ padding: 12, borderRadius: 6, background: 'rgba(168,85,247,0.1)', marginBottom: 12 }}>
            <div style={{ fontWeight: 600 }}>Bank transfer due: {c.refundCzk} Kč</div>
            <div style={{ fontSize: '0.85rem', margin: '4px 0' }}>Account: {revealed ?? c.bankAccount ?? '—'}{' '}
              {!revealed && <button className="btn btn-secondary btn-sm" onClick={() => revealCaseBank(c.id).then((r) => setRevealed(r.bankAccount))}>Show full number</button>}
            </div>
            <div className="text-muted" style={{ fontSize: '0.8rem' }}>Make the transfer from the bank, then confirm here — the account number is deleted on confirm.</div>
            <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} disabled={busy} onClick={() => { if (confirm(`Confirm: ${c.refundCzk} Kč has been transferred?`)) void run(() => markCasePaid(c.id)); }}>Transfer sent ✓</button>
          </div>
        )}
        {c.status === 'waiting_bank_account' && (
          <div className="text-muted" style={{ fontSize: '0.85rem', marginBottom: 12 }}>Waiting for the customer to enter their account number ({c.refundCzk} Kč). The link in their email is valid 14 days.</div>
        )}

        {!mode && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button className="btn btn-secondary" disabled={busy} onClick={() => setMode('message')}>✉ Write to customer</button>
            {c.session?.hasPaymentIntent && c.status !== 'to_pay' && (
              <button className="btn btn-primary" disabled={busy} onClick={() => setMode('refund_card')}>💳 Refund to card (Stripe)</button>
            )}
            {c.status !== 'to_pay' && c.status !== 'waiting_bank_account' && (
              <button className="btn btn-primary" disabled={busy} onClick={() => setMode('bank')}>🏦 Refund by bank transfer</button>
            )}
            <button className="btn btn-secondary" disabled={busy} onClick={() => setMode('close')}>✓ Close without refund</button>
            <button className="btn btn-secondary" disabled={busy} onClick={() => setMode('reject')}>✗ Reject</button>
          </div>
        )}

        {mode && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontWeight: 600 }}>
              {mode === 'message' && 'Message to customer'}
              {mode === 'refund_card' && `Refund to card — order paid ${c.session?.priceCzk ?? '?'} Kč`}
              {mode === 'bank' && 'Refund by bank transfer — the customer gets an email with a one-time link to enter their account'}
              {mode === 'close' && 'Close without refund'}
              {mode === 'reject' && 'Reject — the customer gets your explanation by email'}
            </div>

            {(mode === 'refund_card' || mode === 'bank') && (
              <label style={{ fontSize: '0.85rem' }}>Amount, Kč
                <input className="form-input" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} />
              </label>
            )}
            {mode === 'close' && (
              <label style={{ fontSize: '0.85rem' }}>Outcome
                <select className="form-input" value={resolution} onChange={(e) => setResolution(e.target.value)}>
                  <option value="already_refunded">Money already came back by itself</option>
                  <option value="no_fault">No fault on our side</option>
                  <option value="duplicate">Duplicate of another complaint</option>
                </select>
              </label>
            )}
            <label style={{ fontSize: '0.85rem' }}>
              {mode === 'message' ? 'Text (goes by email)' : 'Text for the customer (goes by email)'}{mode !== 'message' && mode !== 'refund_card' && mode !== 'bank' ? ' — required' : ''}
              <span className="text-muted"> · sent as written — the email adds no greeting; customer language: <b>{c.lang}</b></span>
              <textarea className="form-input" rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder={mode === 'message' ? 'Dobrý den, …' : ''} />
            </label>
            <TemplatePicker onInsert={(body) => setText((t) => (t.trim() ? `${t.replace(/\s+$/, '')}\n\n${body}` : body))} />
            {mode === 'message' && (
              <label style={{ fontSize: '0.85rem', display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="checkbox" checked={ask} onChange={(e) => setAsk(e.target.checked)} /> I need an answer (status → waiting for customer)
              </label>
            )}
            {mode !== 'message' && (
              <label style={{ fontSize: '0.85rem' }}>Root cause (internal, for statistics) — required
                <select className="form-input" value={cause} onChange={(e) => setCause(e.target.value)}>
                  <option value="">— pick —</option>
                  {Object.entries(ROOT_CAUSE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
            )}
            <div className="flex gap-2">
              <button className={`btn btn-sm ${mode === 'reject' ? 'btn-danger' : 'btn-primary'}`} disabled={busy || (mode !== 'message' && !causeOk) || ((mode === 'close' || mode === 'reject' || mode === 'message') && !text.trim())}
                onClick={() => {
                  const czk = Number(amount);
                  const go = async () => {
                    if (mode === 'message') return messageCase(c.id, text, ask);
                    if (mode === 'refund_card') {
                      if (!confirm(`Refund ${czk} Kč to the customer's card via Stripe? This cannot be undone.`)) throw new Error('cancelled');
                      return refundCaseCard(c.id, czk, text, cause);
                    }
                    if (mode === 'bank') return requestCaseBank(c.id, czk, text, cause);
                    if (mode === 'close') return resolveCase(c.id, resolution, text, cause);
                    return rejectCase(c.id, text, cause);
                  };
                  void run(go).then(reset);
                }}>
                {mode === 'message' ? 'Send' : mode === 'refund_card' ? `Refund ${amount || '?'} Kč` : mode === 'bank' ? 'Send bank link' : mode === 'close' ? 'Close' : 'Reject'}
              </button>
              <button className="btn btn-secondary btn-sm" disabled={busy} onClick={reset}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Reply templates: pick one to insert into the text, or manage them right here.
 * Stored on the server (shared by all operators), no language field — put the
 * language in the title («CS · omluva») and pick by the customer's language.
 */
function TemplatePicker({ onInsert }: { onInsert: (body: string) => void }) {
  const [items, setItems] = useState<CaseTemplate[] | null>(null);
  const [manage, setManage] = useState(false);
  const [editing, setEditing] = useState<{ id: string | null; title: string; body: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => listCaseTemplates().then(setItems).catch((e) => setErr(e instanceof Error ? e.message : 'Failed to load templates')), []);
  useEffect(() => { void load(); }, [load]);

  const save = async () => {
    if (!editing) return;
    const title = editing.title.trim(); const body = editing.body.trim();
    if (!title || !body) { setErr('Title and text are required'); return; }
    try {
      if (editing.id) await updateCaseTemplate(editing.id, title, body); else await createCaseTemplate(title, body);
      setEditing(null); setErr(null); await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Save failed'); }
  };
  const remove = async (t: CaseTemplate) => {
    if (!confirm(`Delete template «${t.title}»?`)) return;
    try { await deleteCaseTemplate(t.id); await load(); } catch (e) { setErr(e instanceof Error ? e.message : 'Delete failed'); }
  };

  return (
    <div style={{ fontSize: '0.85rem', display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="text-muted">Insert template:</span>
        {items === null && !err && <span className="text-muted">loading…</span>}
        {items?.length === 0 && !manage && <span className="text-muted">none yet</span>}
        {items?.map((t) => (
          <button key={t.id} type="button" className="btn btn-secondary btn-sm" title={t.body} onClick={() => onInsert(t.body)}>{t.title}</button>
        ))}
        <button type="button" className="btn btn-sm" style={{ marginLeft: 'auto' }} onClick={() => { setManage((m) => !m); setEditing(null); }}>
          {manage ? 'Done' : 'Manage templates'}
        </button>
      </div>
      {err && <div style={{ color: 'var(--danger, #c62027)' }}>{err}</div>}
      {manage && (
        <div style={{ border: '1px solid rgba(128,128,128,0.3)', borderRadius: 6, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items?.map((t) => (
            <div key={t.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{t.title}</div>
                <div className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>{t.body}</div>
              </div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing({ id: t.id, title: t.title, body: t.body })}>Edit</button>
              <button type="button" className="btn btn-danger btn-sm" onClick={() => void remove(t)}>Delete</button>
            </div>
          ))}
          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <input className="form-input" placeholder="Title, e.g. «CS · omluva»" maxLength={80} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} />
              <textarea className="form-input" rows={4} placeholder="Dobrý den, omlouváme se za potíže…" maxLength={4000} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} />
              <div className="flex gap-2">
                <button type="button" className="btn btn-primary btn-sm" onClick={() => void save()}>{editing.id ? 'Save' : 'Add'}</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setEditing(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn btn-secondary btn-sm" style={{ alignSelf: 'flex-start' }} onClick={() => setEditing({ id: null, title: '', body: '' })}>+ New template</button>
          )}
        </div>
      )}
    </div>
  );
}
